import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { GraphQLTimesheetService } from '../../services/graphqlTimesheet';
import {
  WeeklyTimesheet, TimeEntry, TimesheetStatus, Project, Task,
  getWeekDates, formatWeekLabel,
} from '../../types/timesheet';
import { notifyTimesheetSubmitted } from '../../services/inAppNotification';
import { emailTimesheetSubmitted } from '../../services/emailNotification';
import RightSidePanel from '../performance/RightSidePanel';
import InlineLoader from '../common/InlineLoader';
import EmptyState from '../common/EmptyState';
import './MyTimesheet.css';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

interface DayRow { date: string; hours: string; description: string; }

const MyTimesheet: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [timesheet, setTimesheet] = useState<WeeklyTimesheet | null>(null);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectTasks, setProjectTasks] = useState<Task[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [customTaskMode, setCustomTaskMode] = useState(false);
  const [showEntryPanel, setShowEntryPanel] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [addingEntry, setAddingEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Multi-day entry state
  const [entryDayRows, setEntryDayRows] = useState<DayRow[]>([{ date: '', hours: '8', description: '' }]);

  const currentWeek = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + weekOffset * 7);
    return getWeekDates(d);
  }, [weekOffset]);

  const handleDatePick = (dateStr: string) => {
    if (!dateStr) return;
    const picked = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    const msPerWeek = 7 * 24 * 60 * 60 * 1000;
    const startOfWeek = (d: Date) => new Date(getWeekDates(d).start + 'T00:00:00');
    const weeksDiff = Math.round((startOfWeek(picked).getTime() - startOfWeek(today).getTime()) / msPerWeek);
    setWeekOffset(Math.min(weeksDiff, 0));
  };

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const allTimesheets = await GraphQLTimesheetService.getMyTimesheets(user.email);
      const ts = allTimesheets.find(t => t.weekStartDate === currentWeek.start) || null;
      setTimesheet(ts);
      if (ts) {
        const e = await GraphQLTimesheetService.getEntriesForTimesheet(ts.id);
        setEntries(e);
      } else {
        setEntries([]);
      }
      const memberships = await GraphQLTimesheetService.getEmployeeProjects(user.email);
      const allProjects = await GraphQLTimesheetService.listProjects();
      const assignedIds = new Set(memberships.map(m => m.projectId));
      setProjects(allProjects.filter(p => assignedIds.has(p.id) && p.status === 'Active'));
    } catch (err) {
      console.error('[MyTimesheet] Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [user, currentWeek.start]);

  useEffect(() => { loadData(); }, [loadData]);

  // Reset day rows when panel opens
  const openEntryPanel = () => {
    setEntryDayRows([{ date: currentWeek.days[0], hours: '8', description: '' }]);
    setSelectedProjectId('');
    setProjectTasks([]);
    setCustomTaskMode(false);
    setShowEntryPanel(true);
  };

  // Load tasks when project is selected
  const handleProjectChange = async (projectId: string) => {
    setSelectedProjectId(projectId);
    if (projectId) {
      try {
        const tasks = await GraphQLTimesheetService.getTasksByProject(projectId);
        setProjectTasks(tasks.filter(t => t.isActive));
      } catch (err) {
        console.warn('[MyTimesheet] Failed to load tasks:', err);
        setProjectTasks([]);
      }
    } else {
      setProjectTasks([]);
    }
  };

  const addDayRow = () => {
    // Find next available day not already selected
    const usedDates = new Set(entryDayRows.map(r => r.date));
    const nextDate = currentWeek.days.find(d => !usedDates.has(d)) || currentWeek.days[0];
    setEntryDayRows(prev => [...prev, { date: nextDate, hours: '8', description: '' }]);
  };

  const removeDayRow = (index: number) => {
    if (entryDayRows.length <= 1) return;
    setEntryDayRows(prev => prev.filter((_, i) => i !== index));
  };

  const updateDayRow = (index: number, field: keyof DayRow, value: string) => {
    setEntryDayRows(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  const gridRows = useMemo(() => {
    const map = new Map<string, { projectName: string; taskName: string; projectId: string; taskId: string; hours: Record<string, number>; entryIds: Record<string, string> }>();
    entries.forEach(e => {
      const key = `${e.projectId}::${e.taskId}`;
      if (!map.has(key)) {
        map.set(key, { projectName: e.projectName, taskName: e.taskName, projectId: e.projectId, taskId: e.taskId, hours: {}, entryIds: {} });
      }
      const row = map.get(key)!;
      row.hours[e.date] = (row.hours[e.date] || 0) + e.hours;
      row.entryIds[e.date] = e.id;
    });
    return Array.from(map.values());
  }, [entries]);

  const dailyTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    currentWeek.days.forEach(d => { totals[d] = 0; });
    entries.forEach(e => { if (totals[e.date] !== undefined) totals[e.date] += e.hours; });
    return totals;
  }, [entries, currentWeek.days]);

  // Only count hours for today and past days (not future days)
  const todayForTotal = useMemo(() => {
    const now = new Date();
    return now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
  }, []);
  const weekTotal = Object.entries(dailyTotals)
    .filter(([date]) => date <= todayForTotal)
    .reduce((s, [, h]) => s + h, 0);

  const getDayClass = (hours: number) => {
    if (hours === 0) return '';
    if (hours < 8) return 'tms-hours-under';
    if (hours <= 10) return 'tms-hours-ok';
    return 'tms-hours-over';
  };

  const handleSubmit = async () => {
    if (!timesheet || !user) return;
    setShowConfirmModal(true);
  };

  const confirmSubmit = async () => {
    if (!timesheet || !user) return;
    setShowConfirmModal(false);
    setSubmitting(true);
    try {
      console.log('[MyTimesheet] Submitting timesheet:', timesheet.id, 'totalHours:', weekTotal);
      const result = await GraphQLTimesheetService.updateTimesheet(timesheet.id, {
        status: TimesheetStatus.SUBMITTED,
        totalHours: weekTotal,
        submittedAt: new Date().toISOString(),
      });
      console.log('[MyTimesheet] Submit result:', result);

      // Send notification to manager + confirmation to self
      const managerRecipient = user.managerEmail || timesheet.managerId || '';
      if (managerRecipient || user.email) {
        try {
          await notifyTimesheetSubmitted(
            managerRecipient,
            user.name,
            formatWeekLabel(currentWeek.start),
            weekTotal,
            timesheet.id,
            user.email  // self-notification
          );
          console.log('[MyTimesheet] Notification sent to manager:', managerRecipient, 'and self:', user.email);
        } catch (notifErr) {
          console.warn('[MyTimesheet] Notification failed:', notifErr);
        }
      }

      // Send email notification (always — to employee, manager, and HR)
      try {
        console.log('[MyTimesheet] ===== TRIGGERING TIMESHEET EMAIL =====');
        console.log('[MyTimesheet] Employee:', user.email, 'Manager:', managerRecipient);
        const emailResult = await emailTimesheetSubmitted(
          user.email,
          user.name,
          managerRecipient || user.email, // fallback to self if no manager
          managerRecipient ? managerRecipient.split('@')[0] : 'Manager',
          formatWeekLabel(currentWeek.start),
          weekTotal
        );
        console.log('[MyTimesheet] Email result:', emailResult);
      } catch (emailErr) {
        console.error('[MyTimesheet] Email notification failed:', emailErr);
      }

      showToast('Timesheet submitted successfully!', 'success');
      await loadData();
    } catch (err: any) {
      console.error('[MyTimesheet] Submit error:', err);
      showToast('Failed to submit timesheet. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateEntries = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user) return;
    const fd = new FormData(e.currentTarget);
    const projectId = fd.get('projectId') as string;
    const taskName = fd.get('taskName') as string;
    const overallDescription = fd.get('description') as string || '';
    const project = projects.find(p => p.id === projectId);

    if (!projectId || !taskName) return;

    // Validate day rows
    const validRows = entryDayRows.filter(r => r.date && parseFloat(r.hours) > 0);
    if (validRows.length === 0) {
      showToast('Please add at least one day with hours.', 'error');
      return;
    }

    // Prevent selecting the same day twice within this submission
    const seenDates = new Set<string>();
    for (const row of validRows) {
      if (seenDates.has(row.date)) {
        showToast(`${row.date} is selected more than once. Combine the hours into a single row.`, 'error');
        return;
      }
      seenDates.add(row.date);
    }

    // Validate daily hour limit (max 10 hours per day including existing entries and other rows in this submission)
    const MAX_DAILY_HOURS = 10;
    for (const row of validRows) {
      const existingHours = dailyTotals[row.date] || 0;
      const newHours = parseFloat(row.hours);
      if (newHours > MAX_DAILY_HOURS) {
        showToast(`Cannot log more than ${MAX_DAILY_HOURS} hours in a single entry. You entered ${newHours}h for ${row.date}.`, 'error');
        return;
      }
      if (existingHours + newHours > MAX_DAILY_HOURS) {
        showToast(`Daily limit exceeded for ${row.date}. Already logged ${existingHours}h + new ${newHours}h = ${existingHours + newHours}h (max ${MAX_DAILY_HOURS}h).`, 'error');
        return;
      }
    }

    // Prevent duplicate same-project/same-task on same day
    for (const row of validRows) {
      const duplicate = entries.find(
        e => e.date === row.date && e.projectId === projectId && e.taskName.toLowerCase() === taskName.toLowerCase()
      );
      if (duplicate) {
        showToast(`Duplicate entry: "${taskName}" already exists on ${row.date} for this project. Edit the existing entry instead.`, 'error');
        return;
      }
    }

    setAddingEntry(true);
    try {
      let ts = timesheet;
      if (!ts) {
        ts = await GraphQLTimesheetService.createWeeklyTimesheet({
          employeeId: user.email,
          employeeName: user.name,
          managerId: user.managerEmail || '',
          weekStartDate: currentWeek.start,
          weekEndDate: currentWeek.end,
        });
        setTimesheet(ts);
      }
      if (!ts) throw new Error('Failed to create timesheet');

      for (const row of validRows) {
        await GraphQLTimesheetService.createTimeEntry({
          weeklyTimesheetId: ts.id,
          employeeId: user.email,
          employeeName: user.name,
          projectId,
          projectName: project?.name || '',
          taskId: taskName.toLowerCase().replace(/\s+/g, '-'),
          taskName,
          date: row.date,
          hours: parseFloat(row.hours),
          description: [row.description, overallDescription].filter(Boolean).join(' — ') || '',
          billable: project?.billable || false,
        });
      }

      setShowEntryPanel(false);
      showToast(`${validRows.length} entr${validRows.length > 1 ? 'ies' : 'y'} added`, 'success');
      await loadData();
    } catch (err) {
      console.error('[MyTimesheet] Create entry error:', err);
      showToast('Failed to add time entry.', 'error');
    } finally {
      setAddingEntry(false);
    }
  };

  const handleEditEntry = (entry: TimeEntry) => { setEditingEntry(entry); };

  const handleUpdateEntry = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingEntry) return;
    const fd = new FormData(e.currentTarget);
    const newHours = parseFloat(fd.get('editHours') as string);

    if (!newHours || isNaN(newHours) || newHours <= 0) {
      showToast('Please enter valid hours (minimum 0.5).', 'error');
      return;
    }

    // Validate daily limit on edit (max 10 hours per day total)
    const MAX_DAILY_HOURS = 10;
    const otherEntriesHours = entries
      .filter(en => en.date === editingEntry.date && en.id !== editingEntry.id)
      .reduce((sum, en) => sum + en.hours, 0);
    if (otherEntriesHours + newHours > MAX_DAILY_HOURS) {
      showToast(`Daily limit exceeded for ${editingEntry.date}. Other entries: ${otherEntriesHours}h + this: ${newHours}h = ${otherEntriesHours + newHours}h (max ${MAX_DAILY_HOURS}h).`, 'error');
      return;
    }

    try {
      await GraphQLTimesheetService.updateTimeEntry({
        id: editingEntry.id,
        hours: newHours,
        description: (fd.get('editDescription') as string || '').trim() || ' ',
      });
      setEditingEntry(null);
      showToast('Entry updated', 'success');
      await loadData();
    } catch (err: any) {
      console.error('[MyTimesheet] Update entry error:', err);
      const detail = err?.errors?.[0]?.message || err?.message;
      showToast(detail ? `Failed to update entry: ${detail}` : 'Failed to update entry.', 'error');
    }
  };

  const handleDeleteEntry = async (entryId: string) => {
    if (!window.confirm('Delete this time entry?')) return;
    // Close modal immediately for better UX
    setEditingEntry(null);
    // Optimistically remove from local state
    setEntries(prev => prev.filter(e => e.id !== entryId));
    try {
      await GraphQLTimesheetService.deleteTimeEntry(entryId);
      showToast('Entry deleted', 'success');
      // Reload to sync with server
      await loadData();
    } catch (err) {
      console.error('[MyTimesheet] Delete error:', err);
      showToast('Failed to delete entry. Refreshing...', 'error');
      // Reload to restore actual state
      await loadData();
    }
  };

  const isDraft = !timesheet || timesheet.status === TimesheetStatus.DRAFT || timesheet.status === TimesheetStatus.RETURNED;

  return (
    <section className="tms-page">
      <div className="tms-header">
        <div className="tms-header__left">
          <h2 className="tms-header__title">My Timesheet</h2>
          {timesheet && <span className={`tms-status-badge tms-status-${timesheet.status.toLowerCase()}`}>{timesheet.status}</span>}
          {timesheet?.status === TimesheetStatus.APPROVED && (
            <span className="tms-approved-note">✅ This timesheet is already approved. You cannot edit it.</span>
          )}
        </div>
        <div className="tms-header__right">
          <div className="tms-date-range-picker">
            <input
              type="date"
              className="tms-date-range-picker__input"
              value={currentWeek.start}
              max={currentWeek.end}
              onChange={e => handleDatePick(e.target.value)}
              aria-label="From date"
            />
            <span className="tms-date-range-picker__sep">to</span>
            <input
              type="date"
              className="tms-date-range-picker__input"
              value={currentWeek.end}
              max={getWeekDates(new Date()).end}
              onChange={e => handleDatePick(e.target.value)}
              aria-label="To date"
            />
          </div>
          {isDraft && <button className="tms-btn tms-btn-primary" onClick={openEntryPanel}>+ Add Entry</button>}
          {isDraft && entries.length > 0 && (
            <button className="tms-btn tms-btn-submit" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Week'}
            </button>
          )}
        </div>
      </div>

      <div className="tms-week-nav">
        <button className="tms-week-nav__btn" onClick={() => setWeekOffset(w => w - 1)}>← Prev</button>
        <span className="tms-week-nav__label">{formatWeekLabel(currentWeek.start)}</span>
        <button className="tms-week-nav__btn" onClick={() => setWeekOffset(w => w + 1)} disabled={weekOffset >= 0}>Next →</button>
      </div>

      {timesheet?.status === TimesheetStatus.RETURNED && timesheet.returnedReason && (
        <div className="tms-returned-banner">⚠️ Returned by manager: {timesheet.returnedReason}</div>
      )}

      {loading ? <InlineLoader message="Loading timesheet" /> : gridRows.length === 0 && !timesheet ? (
        <EmptyState icon="rocket" title="No timesheet for this week" subtitle="Start logging your hours by adding a time entry." actionLabel="+ Add Entry" onAction={openEntryPanel} />
      ) : (
        <div className="tms-grid-wrapper">
          {(() => {
            const now = new Date();
            const todayStr = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
            return (
          <table className="tms-grid">
            <thead>
              <tr>
                <th className="tms-grid__project-col">Project / Task</th>
                {currentWeek.days.map((d, i) => {
                  const isFuture = d > todayStr;
                  return (
                    <th key={d} className={`tms-grid__day-col ${isFuture ? 'tms-grid__day-future' : ''}`}>
                      {DAY_LABELS[i]}<br /><span className="tms-grid__date">{d.slice(5)}</span>
                      {isFuture && <span className="tms-grid__future-label">🔒</span>}
                    </th>
                  );
                })}
                <th className="tms-grid__total-col">Total</th>
                {isDraft && <th className="tms-grid__action-col"></th>}
              </tr>
            </thead>
            <tbody>
              {gridRows.map(row => {
                const rowTotal = currentWeek.days.filter(d => d <= todayForTotal).reduce((s, d) => s + (row.hours[d] || 0), 0);
                return (
                  <tr key={`${row.projectId}::${row.taskId}`}>
                    <td className="tms-grid__project-cell">
                      <span className="tms-grid__project-name">{row.projectName}</span>
                      <span className="tms-grid__task-name">{row.taskName}</span>
                    </td>
                    {currentWeek.days.map(d => {
                      const isFuture = d > todayStr;
                      return (
                        <td key={d} className={`tms-grid__hour-cell ${isFuture ? 'tms-grid__cell-future' : getDayClass(row.hours[d] || 0)}`}>
                          {isFuture ? <span className="tms-future-text">Can't edit</span> : (row.hours[d] || '—')}
                        </td>
                      );
                    })}
                    <td className="tms-grid__total-cell">{rowTotal}</td>
                    {isDraft && (
                      <td className="tms-grid__actions-cell">
                        <button className="tms-grid__edit-btn" title="Edit" onClick={() => {
                          const firstDate = currentWeek.days.find(d => row.entryIds[d]);
                          if (firstDate) { const entry = entries.find(en => en.id === row.entryIds[firstDate]); if (entry) handleEditEntry(entry); }
                        }}>✏️</button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="tms-grid__totals-row">
                <td>Daily Total</td>
                {currentWeek.days.map(d => {
                  const isFuture = d > todayStr;
                  return <td key={d} className={isFuture ? 'tms-grid__cell-future' : getDayClass(dailyTotals[d])}>{isFuture ? '—' : (dailyTotals[d] || '—')}</td>;
                })}
                <td className="tms-grid__grand-total">{weekTotal}h</td>
                {isDraft && <td></td>}
              </tr>
            </tfoot>
          </table>
            );
          })()}
        </div>
      )}

      {/* Add Entry Panel */}
      <RightSidePanel isOpen={showEntryPanel} title="Add Time Entry" onClose={() => setShowEntryPanel(false)} wide>
        <form className="tms-entry-form" onSubmit={handleCreateEntries}>
          <div className="tms-entry-form__field">
            <label>Project *</label>
            <select name="projectId" required value={selectedProjectId} onChange={e => handleProjectChange(e.target.value)}>
              <option value="">Select project</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name} ({p.code})</option>)}
            </select>
          </div>
          <div className="tms-entry-form__field">
            <label>Task / Activity *</label>
            {projectTasks.length > 0 && !customTaskMode ? (
              <select name="taskName" required className="tms-entry-form__task-select" onChange={e => { if (e.target.value === '__custom__') { setCustomTaskMode(true); e.target.value = ''; } }}>
                <option value="">Select a task</option>
                {projectTasks.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                <option value="__custom__">✏️ Type custom task...</option>
              </select>
            ) : (
              <div className="tms-entry-form__custom-task">
                <input name="taskName" type="text" required placeholder="e.g. Development, Code Review, Bug Fix" />
                {projectTasks.length > 0 && (
                  <button type="button" className="tms-btn-sm tms-btn-sm-gray" onClick={() => setCustomTaskMode(false)}>← Back to list</button>
                )}
              </div>
            )}
          </div>

          {/* Multi-day rows */}
          <div className="tms-entry-form__days-section">
            <div className="tms-entry-form__days-header">
              <label>Days & Hours *</label>
              <button type="button" className="tms-btn-sm tms-btn-sm-green" onClick={addDayRow} disabled={entryDayRows.length >= 5}>
                + Add Day
              </button>
            </div>
            {entryDayRows.map((row, idx) => {
              const today = new Date();
              const todayStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
              return (
                <div key={idx} className="tms-entry-form__day-row">
                  <select value={row.date} onChange={e => updateDayRow(idx, 'date', e.target.value)} required>
                    <option value="">Select day</option>
                    {currentWeek.days.map((d, i) => {
                      const isFuture = d > todayStr;
                      const isTakenByOtherRow = entryDayRows.some((r, ri) => ri !== idx && r.date === d);
                      return (
                        <option key={d} value={d} disabled={isFuture || isTakenByOtherRow}>
                          {DAY_LABELS[i]} ({d}){isFuture ? ' — can\'t fill future days' : isTakenByOtherRow ? ' — already added' : ''}
                        </option>
                      );
                    })}
                  </select>
                  <input
                    type="number" step="0.5" min="0.5" max="10" required
                    value={row.hours} onChange={e => updateDayRow(idx, 'hours', e.target.value)}
                    placeholder="8"
                  />
                  <span className="tms-entry-form__day-unit">hrs</span>
                  <input
                    type="text"
                    className="tms-entry-form__day-desc"
                    value={row.description}
                    onChange={e => updateDayRow(idx, 'description', e.target.value)}
                    placeholder="Description (optional)"
                  />
                  {entryDayRows.length > 1 && (
                    <button type="button" className="tms-entry-form__day-remove" onClick={() => removeDayRow(idx)}>✕</button>
                  )}
                </div>
              );
            })}
            <p className="tms-entry-form__day-note" style={{ display: 'none' }}>⚠️ You cannot fill timesheets for future days.</p>
          </div>

          <div className="tms-entry-form__field">
            <label>Description (optional)</label>
            <textarea name="description" rows={2} placeholder="Overall notes for this entry..." />
          </div>

          <div className="tms-entry-form__field">
            <label>Reporting Manager</label>
            <input type="text" value={user?.managerEmail || 'Not assigned'} disabled className="tms-entry-form__readonly" />
          </div>
          <div className="tms-entry-form__actions">
            <button type="button" className="tms-btn tms-btn-outline" onClick={() => setShowEntryPanel(false)}>Cancel</button>
            <button type="submit" className="tms-btn tms-btn-primary" disabled={addingEntry}>
              {addingEntry ? '⏳ Adding...' : `Add ${entryDayRows.length > 1 ? `${entryDayRows.length} Entries` : 'Entry'}`}
            </button>
          </div>
        </form>
      </RightSidePanel>

      {/* Edit Entry Modal */}
      {editingEntry && (
        <div className="tms-edit-overlay" onClick={() => setEditingEntry(null)}>
          <div className="tms-edit-modal" onClick={e => e.stopPropagation()}>
            <div className="tms-edit-modal__header">
              <h3>Edit — {editingEntry.projectName} / {editingEntry.taskName}</h3>
              <button className="tms-edit-modal__close" onClick={() => setEditingEntry(null)}>✕</button>
            </div>
            <form onSubmit={handleUpdateEntry}>
              <div className="tms-entry-form__field">
                <label>Date</label>
                <input type="text" value={editingEntry.date} disabled />
              </div>
              <div className="tms-entry-form__row">
                <div className="tms-entry-form__field">
                  <label>Hours *</label>
                  <input name="editHours" type="number" step="0.5" min="0.5" max="10" required defaultValue={editingEntry.hours} />
                </div>
                <div className="tms-entry-form__field">
                  <label>Description</label>
                  <input name="editDescription" type="text" defaultValue={editingEntry.description} placeholder="Notes" />
                </div>
              </div>
              <div className="tms-entry-form__actions">
                <button type="button" className="tms-btn tms-btn-outline tms-btn-danger" onClick={() => handleDeleteEntry(editingEntry.id)}>Delete</button>
                <button type="button" className="tms-btn tms-btn-outline" onClick={() => setEditingEntry(null)}>Cancel</button>
                <button type="submit" className="tms-btn tms-btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && <div className={`tms-toast tms-toast-${toast.type}`} role="alert">{toast.message}</div>}

      {/* Custom Confirm Modal */}
      {showConfirmModal && (
        <div className="tms-confirm-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="tms-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="tms-confirm-modal__icon">📋</div>
            <h3 className="tms-confirm-modal__title">Submit Timesheet</h3>
            <p className="tms-confirm-modal__message">
              Submit this week's timesheet ({weekTotal}h) for approval?<br />
              <span className="tms-confirm-modal__sub">Once submitted, you won't be able to edit until your manager returns it.</span>
            </p>
            <div className="tms-confirm-modal__actions">
              <button className="tms-btn tms-btn-outline" onClick={() => setShowConfirmModal(false)}>Cancel</button>
              <button className="tms-btn tms-btn-primary" onClick={confirmSubmit}>Submit</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default MyTimesheet;
